<script setup lang="ts">
import { onBeforeMount, onMounted, toRefs, ref, watch } from 'vue';
import { useDownload } from '@/composables';
import * as pdfjsLib from 'pdfjs-dist/webpack';
import { EventBus, PDFLinkService, PDFViewer, NullL10n, ProgressBar } from 'pdfjs-dist/web/pdf_viewer';
import type { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import 'pdfjs-dist/web/pdf_viewer.css';

interface Props {
  src: string;
}

interface AttachmentItem {
  filename: string;
  content: Uint8Array;
}

const props = defineProps<Props>();
const { src } = toRefs(props);

let pdfDocument: PDFDocumentProxy | null = null;
let pdfViewer: PDFViewer | null = null;
let loadingBar: ProgressBar | null = null;

const container = ref<HTMLDivElement | null>(null);
const loadingBarContainer = ref<HTMLDivElement | null>(null);
const attachmentsSidebarIsOpen = ref<boolean>(true);
const attachments = ref<AttachmentItem[]>([]);
const totalPages = ref<number>(0);
const currentPage = ref<number>(1);

const eventBus = new EventBus();
const pdfLinkService = new PDFLinkService({ eventBus });

const clamp = (n: number) => Math.min(Math.max(1, n || 1), totalPages.value || 1);

const downloadAttachmentItem = (item: AttachmentItem) => {
  const fileName = item.filename.replace(/(\.\w+)+$/, '');
  useDownload(item.content, fileName, 'mp3');
};

const toggleAttachmentsSidebar = () => {
  attachmentsSidebarIsOpen.value = !attachmentsSidebarIsOpen.value;
};

const zoomIn = () => pdfViewer?.increaseScale();
const zoomOut = () => pdfViewer?.decreaseScale();

const goToNextPage = () => {
  if (!pdfViewer) return;
  pdfViewer.nextPage();
  currentPage.value = pdfViewer.currentPageNumber;
};

const goToPreviousPage = () => {
  if (!pdfViewer) return;
  pdfViewer.previousPage();
  currentPage.value = pdfViewer.currentPageNumber;
};

watch(currentPage, (newPageNumber) => {
  if (!pdfViewer) return;
  const page = clamp(newPageNumber);
  if (page !== pdfViewer.currentPageNumber) {
    pdfViewer.currentPageNumber = page;
    currentPage.value = page;
  }
});

const progress = (level: number) => {
  if (!loadingBar) return;
  const percent = Math.round((Number.isFinite(level) ? level : 0) * 100);
  if (percent > (loadingBar.percent ?? 0)) loadingBar.percent = percent;
  if (percent >= 100 && loadingBarContainer.value) {
    loadingBarContainer.value.style.display = 'none';
  }
};

const initPDFViewer = () => {
  if (!container.value) throw new Error('PDF container not ready');
  return new PDFViewer({
    container: container.value,
    eventBus,
    useOnlyCssZoom: true,
    textLayerMode: 0,
    linkService: pdfLinkService,
    maxCanvasPixels: 8000000,
    l10n: NullL10n,
  });
};

const pdfDocumentLoad = (url: string) => {
  const loadingTask = pdfjsLib.getDocument(url);

  loadingTask.onProgress = (progressData: { loaded: number; total: number }) => {
    progress(progressData.loaded / progressData.total);

    if (progressData.loaded === progressData.total) {
      loadingBarContainer.value.style.display = 'none';
    }
  };

  return loadingTask.promise;
};

const initDocument = async (viewer: PDFViewer | null, url: string) => {
  if (!viewer) throw new Error('PDF viewer not initialised');
  pdfDocument = await pdfDocumentLoad(url);
  viewer.setDocument(pdfDocument);
  pdfLinkService.setDocument(pdfDocument, null);
};

let onPagesInit: () => void;
let onPageChanging: (arg: { pageNumber: number }) => void;

onBeforeMount(() => {
  document.body.classList.add('max-w-full');
});

onMounted(async () => {
  if (loadingBarContainer.value) loadingBar = new ProgressBar(loadingBarContainer.value);
  pdfViewer = initPDFViewer();
  pdfLinkService.setViewer(pdfViewer);

  onPagesInit = () => {
    if (!pdfViewer) return;
    pdfViewer.currentScaleValue = 'page-height';
    currentPage.value = pdfViewer.currentPageNumber;
    totalPages.value = pdfViewer.pagesCount;

    if (typeof src.value === 'string' && src.value.startsWith('blob:')) {
      try {
        window.URL.revokeObjectURL(src.value);
      } catch {
        // Failed to revoke object URL
      }
    }
  };

  onPageChanging = ({ pageNumber }: { pageNumber: number }) => {
    currentPage.value = pageNumber;
  };

  eventBus.on('pagesinit', onPagesInit);
  eventBus.on('pagechanging', onPageChanging);

  try {
    await initDocument(pdfViewer, src.value);

    pdfDocument
      ?.getAttachments?.()
      .then((results) => {
        attachments.value = results ? (Object.values(results) as AttachmentItem[]) : [];
      })
      .catch(() => {
        // Error getting attachments
      });
  } catch (e) {
    console.error("PDF URL expired or invalid. The PDF.js library can't open this URL.", e);
    alert(
      'Sorry, you do not have permission to access this PDF or the URL is invalid. If you think this is a mistake please contact Support.',
    );
  }
});

watch(
  () => src.value,
  async (n) => {
    if (!n) return;
    try {
      await initDocument(pdfViewer, n);
      pdfDocument
        ?.getAttachments?.()
        .then((results) => {
          attachments.value = results ? (Object.values(results) as AttachmentItem[]) : [];
        })
        .catch((e) => {
          console.error('Error getting attachments', e);
        });
    } catch (e) {
      console.error('Error reloading PDF', e);
    }
  },
);
</script>

<template>
  <div class="flex w-full min-h-screen h-screen">
    <div
      v-if="attachments.length > 0"
      class="flex-column bg-gray-800 border-right-1 border-gray-700 w-15rem"
      :class="{ flex: attachmentsSidebarIsOpen, hidden: !attachmentsSidebarIsOpen }"
    >
      <h3 class="px-3 pt-3 mb-2 font-bold text-gray-400">Attachments</h3>
      <ul class="px-0 h-full flex flex-auto flex-column w-full text-sm overflow-auto">
        <li v-for="attachment in attachments" :key="attachment.filename">
          <button
            type="button"
            class="btn-hover btn-square-hover cursor-pointer bg-transparent border-none flex w-full text-gray-300 px-3 py-1"
            title="Click to Download"
            @click="downloadAttachmentItem(attachment)"
          >
            <span class="block white-space-nowrap overflow-hidden text-overflow-ellipsis">
              {{ attachment.filename }}
            </span>
          </button>
        </li>
      </ul>
    </div>

    <div class="flex-initial w-full flex flex-column">
      <div
        class="relative text-gray-300 bg-gray-800 p-2 inline-flex justify-content-center align-items-center gap-1 border-bottom-1 border-gray-700"
      >
        <button
          v-if="attachments.length > 0"
          title="Show/Hide Attachments"
          type="button"
          class="btn-hover text-center bg-gray-800 border-1 border-gray-500 text-gray-300 absolute left-0 ml-2"
          @click="toggleAttachmentsSidebar"
        >
          Attachments
        </button>

        <button
          title="Previous Page"
          type="button"
          class="btn-hover btn-square px-0 text-center bg-gray-800 border-1 border-gray-500 text-gray-300"
          @click="goToPreviousPage"
        >
          &lsaquo;
        </button>
        <button
          title="Next Page"
          type="button"
          class="btn-hover btn-square px-0 text-center bg-gray-800 border-1 border-gray-500 text-gray-300"
          @click="goToNextPage"
        >
          &rsaquo;
        </button>

        <input
          v-model.number.lazy="currentPage"
          :max="totalPages"
          title="Page"
          type="text"
          class="bg-gray-800 border-1 border-gray-500 text-gray-300 w-2rem"
        />
        <span class="text-xs">of {{ totalPages }}</span>

        <button
          title="Zoom Out"
          type="button"
          class="btn-hover btn-square px-0 text-center bg-gray-800 border-1 border-gray-500 text-gray-300"
          @click="zoomOut"
        >
          -
        </button>
        <button
          title="Zoom In"
          type="button"
          class="btn-hover btn-square px-0 text-center bg-gray-800 border-1 border-gray-500 text-gray-300"
          @click="zoomIn"
        >
          +
        </button>
      </div>

      <div ref="loadingBarContainer" class="loading-bar relative border-bottom-1 border-gray-700">
        <div class="progress absolute left-0 overflow-hidden w-full h-full">
          <div class="glimmer absolute top-0 left-0 h-full w-5rem"></div>
        </div>
      </div>

      <div class="relative flex-auto bg-gray-900">
        <div id="viewerContainer" ref="container" class="overflow-auto absolute w-full h-full">
          <div id="viewer" class="pdfViewer"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:global(.pdfViewer .page) {
  box-sizing: content-box !important;
}

.btn-square {
  width: 19px;
  height: 19px;
}

.btn-hover:hover {
  background-color: var(--gray-700) !important;
}

.loading-bar {
  --progressBar-percent: 0%;
  height: 0.35rem;
  background-color: rgba(51, 51, 51, 1);

  .progress {
    transform: scaleX(var(--progressBar-percent));
    transform-origin: 0 0;
    transition: transform 200ms;
    background-color: rgba(221, 221, 221, 1);
  }

  &.indeterminate .progress {
    transform: none;
    transition: none;
    background-color: rgba(153, 153, 153, 1);

    .glimmer {
      background-image: linear-gradient(
        to right,
        rgba(153, 153, 153, 1) 0%,
        rgba(255, 255, 255, 1) 50%,
        rgba(153, 153, 153, 1) 100%
      );
      background-size: 100% 100%;
      background-repeat: no-repeat;
      animation: progressIndeterminate 2s linear infinite;
    }
  }
}

@keyframes progressIndeterminate {
  0% {
    transform: translateX(0%);
  }
  50% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(100%);
  }
}
</style>
